import { inject } from '@angular/core';
import { patchState, signalStoreFeature, type, withMethods } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { exhaustMap, pipe, tap } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { IEmployeeState } from './employee.state';
import { withEmployeeStateMethods } from './employee.methods.state';
import { EmployeeService } from '../../services/employee/employee.service';
import { NotificationService } from '../../services/notification/notification.service';
import { GlobalSpinnerService } from '../../services/global-spinner/global-spinner.service';
import { Employee } from '../../models/employee/employee.model';
import { CreateEmployee } from '../../models/employee/create-employee.model';
import { EditEmployee } from '../../models/employee/edit-employee.model';

export const withEmployeeApiMethods = signalStoreFeature(
  { state: type<IEmployeeState>() },
  withEmployeeStateMethods,
  withMethods((store) => {
    const employeeService = inject(EmployeeService);
    const notificationService = inject(NotificationService);

    const loadEmployees = rxMethod<void>(
      pipe(
        tap(() => {
          patchState(store, { loading: true, error: null });
        }),
        exhaustMap(() =>
          employeeService.getEmployees().pipe(
            tapResponse({
              next: (response: { employees: Employee[] }) => {
                store.setEmployees(response.employees);
              },
              error: (error: HttpErrorResponse) => {
                const message = error.error?.message ?? error.message;
                store.setError(message);
                notificationService.notifyError('Error', message);
              },
            })
          )
        )
      )
    );

    const loadEmployeeByGuid = rxMethod<string>(
      pipe(
        tap(() => patchState(store, { loading: true, error: null })),
        exhaustMap((employeeGuid) =>
          employeeService.getEmployeeByGuid(employeeGuid).pipe(
            tapResponse({
              next: (response: { employee: Employee | null }) => {
                store.setSelectedEmployee(response.employee);
              },
              error: (error: HttpErrorResponse) => {
                const message = error.error?.message ?? error.message;
                store.setError(message);
                notificationService.notifyError('Error', message);
              },
            })
          )
        )
      )
    );

    const createEmployee = rxMethod<CreateEmployee>(
      pipe(
        tap(() => {
          patchState(store, { loading: true, error: null });
        }),
        exhaustMap((payload) =>
          employeeService.createEmployee(payload).pipe(
            tapResponse({
              next: (response: { employee: Employee }) => {
                store.addEmployee(response.employee);
                notificationService.notify('success', 'Success', 'Employee created successfully.');
              },
              error: (error: HttpErrorResponse) => {
                const message = error.error?.message ?? error.message;
                store.setError(message);
                notificationService.notifyError('Error', message);
              },
            })
          )
        )
      )
    );

    const editEmployee = rxMethod<{ employeeGuid: string; payload: EditEmployee }>(
      pipe(
        tap(() => {
          patchState(store, { loading: true, error: null });
        }),
        exhaustMap(({ employeeGuid, payload }) =>
          employeeService.editEmployee(employeeGuid, payload).pipe(
            tapResponse({
              next: (response: { employee: Employee }) => {
                store.updateEmployee(response.employee);
                notificationService.notify('success', 'Success', 'Employee updated successfully.');
              },
              error: (error: HttpErrorResponse) => {
                const message = error.error?.message ?? error.message;
                store.setError(message);
                notificationService.notifyError('Error', message);
              },
            })
          )
        )
      )
    );

    const deleteEmployee = rxMethod<string>(
      pipe(
        tap(() => {
          patchState(store, { loading: true, error: null });
        }),
        exhaustMap((employeeGuid) =>
          employeeService.deleteEmployee(employeeGuid).pipe(
            tapResponse({
              next: () => {
                store.removeEmployee(employeeGuid);
                notificationService.notify('success', 'Success', 'Employee deleted successfully.');
              },
              error: (error: HttpErrorResponse) => {
                const message = error.error?.message ?? error.message;
                store.setError(message);
                notificationService.notifyError('Error', message);
              },
            })
          )
        )
      )
    );

    return { loadEmployees, loadEmployeeByGuid, createEmployee, editEmployee, deleteEmployee };
  })
);
